import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Button, TextField, Typography, Paper, MenuItem,
  Divider, Snackbar, Alert, Stepper, Step, StepLabel,
  CircularProgress, InputAdornment, IconButton,
  Card, CardContent, Grid
} from '@mui/material';
import { Visibility, VisibilityOff, CloudUpload, CheckCircle } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Register } from '../hooks/hooks';

const genderOptions = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'O', label: 'Other' }
];

const steps = ['Personal Info', 'Business Details', 'Verification'];

const validationSchema = Yup.object({
  username: Yup.string().min(3, 'Username must be at least 3 characters').required('Username is required'),
  email: Yup.string().email('Invalid email format').required('Email is required'),
  phone: Yup.string().matches(/^\+?[\d\s-]+$/, 'Invalid phone number').required('Phone is required'),
  zip_code: Yup.string().required('Zip code is required'),
  country: Yup.string().required('Country is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Must contain uppercase, lowercase and number')
    .required('Password is required'),
  confirm_password: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password is required'),
  nida_id: Yup.string().required('NIDA ID is required'),
  business_name: Yup.string().required('Business name is required'),
  business_registration_number: Yup.string().required('Registration number is required'),
  tax_id: Yup.string().required('Tax ID is required'),
  address: Yup.string().required('Address is required'),
  gender: Yup.string().oneOf(genderOptions.map(g => g.value)).required('Gender is required'),
  date_of_birth: Yup.date()
    .max(new Date(Date.now() - 18 * 365 * 24 * 60 * 60 * 1000), 'Must be at least 18 years old')
    .required('Date of birth is required'),
  referral_code: Yup.string(),
  id_front_url: Yup.string().required('ID front image is required'),
  id_back_url: Yup.string().required('ID back image is required'),
  id_selfie_url: Yup.string().required('Selfie with ID is required'),
});

const Registration = () => {
  const [countries, setCountries] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    axios.get('https://restcountries.com/v3.1/all?fields=name,cca2')
      .then(response => {
        const countryList = response.data.map(country => ({
          name: country.name.common,
          code: country.cca2
        })).sort((a, b) => a.name.localeCompare(b.name));
        setCountries(countryList);
      })
      .catch(error => {
        console.error('Error fetching countries:', error);
      });
  }, []);

  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: Register,
    onSuccess: (response) => {
      const message = response.message;
      setLoading(false);
      setSnackbar({ open: true, message: message, severity: 'success' });
      setTimeout(() => navigate('/login'), 2000);
    },
    onError: (error) => {
      setLoading(false);
      setSnackbar({ open: true, message: error.message || 'Registration failed', severity: 'error' });
    }
  });

  const formik = useFormik({
    initialValues: {
      username: '',
      user_type: 'business',
      email: '',
      phone: '',
      zip_code: '',
      country: '',
      password: '',
      confirm_password: '',
      nida_id: '',
      business_name: '',
      business_registration_number: '',
      tax_id: '',
      address: '',
      gender: '',
      date_of_birth: '',
      referral_code: '',
      id_front_url: '',
      id_back_url: '',
      id_selfie_url: '',
    },
    validationSchema,
    onSubmit: (values) => {
      setLoading(true);
      mutation.mutate(values);
    }
  });

  const handleNext = () => {
    let fieldsToValidate = [];
    switch (activeStep) {
      case 0:
        fieldsToValidate = ['username', 'email', 'phone', 'date_of_birth', 'gender', 'zip_code', 'country', 'password', 'confirm_password'];
        break;
      case 1:
        fieldsToValidate = ['business_name', 'business_registration_number', 'tax_id', 'nida_id', 'address'];
        break;
      case 2:
        fieldsToValidate = ['id_front_url', 'id_back_url', 'id_selfie_url'];
        break;
      default:
        break;
    }

    const errors = fieldsToValidate.filter(field => formik.errors[field] && formik.touched[field]);
    if (errors.length === 0) {
      setActiveStep((prev) => prev + 1);
    } else {
      // Touch all fields to show errors
      fieldsToValidate.forEach(field => {
        if (!formik.touched[field]) {
          formik.setFieldTouched(field, true);
        }
      });
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleFileUpload = (field, url) => {
    formik.setFieldValue(field, url);
    formik.setFieldTouched(field, true);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                label="Username"
                name="username"
                value={formik.values.username}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.username && Boolean(formik.errors.username)}
                helperText={formik.touched.username && formik.errors.username}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                label="Phone"
                name="phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                label="Date of Birth"
                name="date_of_birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formik.values.date_of_birth}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.date_of_birth && Boolean(formik.errors.date_of_birth)}
                helperText={formik.touched.date_of_birth && formik.errors.date_of_birth}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                select
                fullWidth
                label="Gender"
                name="gender"
                value={formik.values.gender}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.gender && Boolean(formik.errors.gender)}
                helperText={formik.touched.gender && formik.errors.gender}
              >
                <MenuItem value="">Select Gender</MenuItem>
                {genderOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                select
                fullWidth
                label="Country"
                name="country"
                value={formik.values.country}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.country && Boolean(formik.errors.country)}
                helperText={formik.touched.country && formik.errors.country}
              >
                <MenuItem value="">Select Country</MenuItem>
                {countries.map((country) => (
                  <MenuItem key={country.code} value={country.name}>
                    {country.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                label="Zip Code"
                name="zip_code"
                value={formik.values.zip_code}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.zip_code && Boolean(formik.errors.zip_code)}
                helperText={formik.touched.zip_code && formik.errors.zip_code}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                label="Referral Code (Optional)"
                name="referral_code"
                value={formik.values.referral_code}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.referral_code && Boolean(formik.errors.referral_code)}
                helperText={formik.touched.referral_code && formik.errors.referral_code}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                label="Confirm Password"
                name="confirm_password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formik.values.confirm_password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.confirm_password && Boolean(formik.errors.confirm_password)}
                helperText={formik.touched.confirm_password && formik.errors.confirm_password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Business Name"
                name="business_name"
                value={formik.values.business_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.business_name && Boolean(formik.errors.business_name)}
                helperText={formik.touched.business_name && formik.errors.business_name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                label="Business Registration Number"
                name="business_registration_number"
                value={formik.values.business_registration_number}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.business_registration_number && Boolean(formik.errors.business_registration_number)}
                helperText={formik.touched.business_registration_number && formik.errors.business_registration_number}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                label="Tax ID"
                name="tax_id"
                value={formik.values.tax_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.tax_id && Boolean(formik.errors.tax_id)}
                helperText={formik.touched.tax_id && formik.errors.tax_id}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                label="NIDA ID"
                name="nida_id"
                value={formik.values.nida_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.nida_id && Boolean(formik.errors.nida_id)}
                helperText={formik.touched.nida_id && formik.errors.nida_id}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                multiline
                rows={3}
                label="Business Address"
                name="address"
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
              />
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={3}>
            {[
              { field: 'id_front_url', label: 'ID Front Image', description: 'Upload the front side of your ID card' },
              { field: 'id_back_url', label: 'ID Back Image', description: 'Upload the back side of your ID card' },
              { field: 'id_selfie_url', label: 'Selfie with ID', description: 'Upload a selfie holding your ID card' }
            ].map((doc, index) => (
              <Grid item xs={12} md={4} key={doc.field}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    border: formik.values[doc.field] ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#1976d2',
                      boxShadow: 1
                    }
                  }}
                  onClick={() => {/* Add file upload logic here */}}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <CloudUpload sx={{ fontSize: 40, color: formik.values[doc.field] ? '#4CAF50' : '#757575', mb: 1 }} />
                    <Typography variant="h6" sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 1 }}>
                      {doc.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 2 }}>
                      {doc.description}
                    </Typography>
                    {formik.values[doc.field] ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4CAF50' }}>
                        <CheckCircle sx={{ fontSize: 16, mr: 0.5 }} />
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          Uploaded
                        </Typography>
                      </Box>
                    ) : (
                      <Button size="small" variant="outlined" startIcon={<CloudUpload />}>
                        Upload
                      </Button>
                    )}
                  </CardContent>
                </Card>
                {formik.touched[doc.field] && formik.errors[doc.field] && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {formik.errors[doc.field]}
                  </Typography>
                )}
              </Grid>
            ))}
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f5f5f5" px={2}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 800, borderRadius: 2 }}>
        <Typography variant="h4" textAlign="center" mb={1} sx={{ fontWeight: 700, color: "#212121" }}>
          API Dashboard Sign-Up
        </Typography>
        <Typography variant="body1" textAlign="center" mb={4} sx={{ color: "#616161" }}>
          Authenticate to unlock API keys, docs & transaction analytics
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <form onSubmit={formik.handleSubmit}>
          {getStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ textTransform: 'none' }}
            >
              Back
            </Button>
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 4,
                    backgroundColor: "#D32F2F",
                    "&:hover": {
                      backgroundColor: "#B71C1C",
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Complete Registration'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 4,
                  }}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </form>

        <Box mt={3} textAlign="center">
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#D32F2F', textDecoration: 'none', fontWeight: 600 }}>
              Login
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default Registration;